// Iter 82.9 Sprint 2 — Payroll Exceptions panel.
// Operator-mandated visual grouping by severity (Critical / Warning / Info).
// Critical (Critical-severity) and Warning (High+Medium) buckets carry
// explicit gate badges showing whether they block Submit / Approve.
// Override flow with mandatory ≥10-char reason.
import { useCallback, useEffect, useState } from "react";
import { Warning, ShieldWarning, Info, Check, ShieldCheck } from "@phosphor-icons/react";
import { api, formatApiError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "../../../components/ui/dialog";
import { toast } from "sonner";

const SEVERITY_TONE = {
    Critical: "bg-rose-50 border-rose-200 text-rose-900",
    High:     "bg-amber-50 border-amber-200 text-amber-900",
    Medium:   "bg-yellow-50 border-yellow-200 text-yellow-900",
    Low:      "bg-slate-50 border-slate-200 text-slate-800",
    Info:     "bg-sky-50 border-sky-200 text-sky-900",
};

const SEVERITY_ICON = {
    Critical: ShieldWarning,
    High: Warning,
    Medium: Warning,
    Low: Info,
    Info: Info,
};

// Operator grouping (per Sprint 2 brief):
//   Critical = severity Critical    → blocks Submit + Approve
//   Warning  = severity High|Medium → blocks Approve (High)
//   Info     = severity Low|Info    → advisory only
const GROUPS = [
    { key: "critical", title: "Critical", severities: ["Critical"], blockerLabel: "Blocks Submit + Approve" },
    { key: "warning",  title: "Warning",  severities: ["High", "Medium"], blockerLabel: "High blocks Approve · Medium is advisory" },
    { key: "info",     title: "Informational", severities: ["Low", "Info"], blockerLabel: "Advisory only — does not block" },
];

export default function PayrollExceptionsPanel({ runId, canEdit, onResolved, calcVersion }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [overrideTarget, setOverrideTarget] = useState(null);
    const [overrideReason, setOverrideReason] = useState("");

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/payroll/run/${runId}/exceptions`);
            setItems(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load exceptions"));
        } finally {
            setLoading(false);
        }
    }, [runId, calcVersion]);

    useEffect(() => { reload(); }, [reload]);

    const submitOverride = async () => {
        if (overrideReason.length < 10) {
            toast.error("Override reason must be at least 10 characters");
            return;
        }
        try {
            await api.post(
                `/payroll/run/${runId}/exceptions/${overrideTarget.id}/override`,
                { reason: overrideReason },
            );
            toast.success(`Exception ${overrideTarget.id} overridden`);
            setOverrideTarget(null);
            setOverrideReason("");
            await reload();
            onResolved && onResolved();
        } catch (err) {
            toast.error(formatApiError(err, "Override failed"));
        }
    };

    const byGroup = GROUPS.map((g) => ({
        ...g,
        rows: items.filter((e) => g.severities.includes(e.severity)),
    }));

    return (
        <div className="space-y-4 pt-3" data-testid="exceptions-panel">
            {loading && <div className="text-sm text-slate-400">Loading…</div>}
            {!loading && items.length === 0 && (
                <div className="border rounded-lg p-6 text-center text-emerald-700 bg-emerald-50" data-testid="exceptions-empty">
                    <ShieldCheck size={24} className="mx-auto mb-1" />
                    No open exceptions on this run.
                </div>
            )}
            {byGroup.map((g) => {
                const open = g.rows.filter((e) => e.status === "Open");
                const overridden = g.rows.filter((e) => e.status === "Overridden");
                if (g.rows.length === 0) return null;
                return (
                    <section key={g.key} className="border rounded-lg bg-white overflow-hidden" data-testid={`exc-group-${g.key}`}>
                        <header className={`px-4 py-3 border-b flex items-center justify-between gap-3 ${g.key === "critical" ? "bg-rose-50" : g.key === "warning" ? "bg-amber-50" : "bg-sky-50"}`}>
                            <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-sm" data-testid={`exc-group-title-${g.key}`}>
                                    {g.title}
                                </h3>
                                <Badge variant="outline" className="text-[10px]">
                                    {open.length} open · {overridden.length} overridden
                                </Badge>
                            </div>
                            <span className="text-[11px] text-slate-600" data-testid={`exc-group-gate-${g.key}`}>{g.blockerLabel}</span>
                        </header>
                        <div className="divide-y divide-slate-100">
                            {g.rows.map((e) => {
                                const Icon = SEVERITY_ICON[e.severity] || Info;
                                const tone = SEVERITY_TONE[e.severity];
                                return (
                                    <article key={e.id} className={`p-3 ${tone}`} data-testid={`exc-row-${e.id}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-start gap-2 flex-1 min-w-0">
                                                <Icon size={18} className="flex-shrink-0 mt-0.5" />
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-mono text-[11px] font-semibold">{e.category}</span>
                                                        <Badge variant="outline" className="text-[10px]">{e.severity}</Badge>
                                                        {e.status === "Overridden" && (
                                                            <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Overridden</Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm mt-1">{e.message}</p>
                                                    {e.employee_id && (
                                                        <p className="text-[11px] text-slate-500 mt-1">Employee: <span className="font-mono">{e.employee_id}</span></p>
                                                    )}
                                                    {e.override_reason && (
                                                        <p className="text-[11px] text-emerald-700 mt-1">
                                                            <Check size={10} className="inline-block mr-0.5" />
                                                            <strong>Override:</strong> {e.override_reason} — by {e.override_user_email}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {e.status === "Open" && canEdit && (
                                                <Button size="sm" variant="outline" onClick={() => setOverrideTarget(e)} data-testid={`exc-override-btn-${e.id}`}>
                                                    Override
                                                </Button>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    </section>
                );
            })}

            <Dialog open={!!overrideTarget} onOpenChange={(v) => !v && setOverrideTarget(null)}>
                <DialogContent data-testid="exc-override-dialog">
                    <DialogHeader>
                        <DialogTitle>Override exception</DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            The exception will be preserved as an
                            audit row — your reason will surface on
                            future regulator queries.
                        </DialogDescription>
                    </DialogHeader>
                    {overrideTarget && (
                        <div className="space-y-2 py-2">
                            <div className="bg-slate-50 border rounded p-2 text-xs">
                                <div className="font-mono font-semibold">{overrideTarget.category}</div>
                                <div>{overrideTarget.message}</div>
                            </div>
                            <Label className="text-xs">Override reason (≥10 chars, mandatory)</Label>
                            <Input
                                value={overrideReason}
                                onChange={(e) => setOverrideReason(e.target.value)}
                                placeholder="e.g. PAN backfill scheduled for next month; payroll cleared by CFO via email."
                                data-testid="exc-override-reason"
                            />
                            <p className="text-[10px] text-slate-500">
                                The exception will not be deleted — it stays as a permanent audit row tagged "Overridden by {`<your name>`}".
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOverrideTarget(null)} data-testid="exc-override-cancel">Cancel</Button>
                        <Button onClick={submitOverride} data-testid="exc-override-confirm">Override + Audit</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
